import { api } from './api';
import { entriesDeleteMany } from '../containers/contentSlice';

export type AskAiMessage = {
	role: 'user' | 'assistant';
	content: string;
};

export type AskAiRequest = {
	query: string;
	/** Current canvas context id (locationId / parentId) or null for root. */
	locationId?: string | null;
	/** From the previous answer; omit to start a new conversation. */
	conversationId?: string | null;
};

export type AskAiResponse = {
	answer: string;
	conversationId: string | null;
	context: {
		toolCalls: {toolName: string; deletedIds?: string[]}[];
	};
};

export type AiConversationSummary = {
	id: string;
	title: string;
	createdAt: string;
	updatedAt: string;
};

export type AiConversationWithMessages = AiConversationSummary & {
	messages: (AskAiMessage & {createdAt: string})[];
};

const ENTRY_WRITE_TOOL_NAMES = new Set([
	'create_repository',
	'add_repository_fields',
	'update_repository_field',
	'create_repository_entries',
	'create_repository_entry',
	'update_repository_entry',
	'update_repository_entries',
	'archive_repository_entry',
	'unarchive_repository_entry',
]);

const BOARD_WRITE_TOOL_NAMES = new Set([
	'create_board_notes',
	'create_board_chart',
	'add_entry_references_to_board',
	'update_board_elements',
	'delete_board_elements',
]);

export const aiApi = api.injectEndpoints({
	endpoints: (builder) => ({
		askAi: builder.mutation<AskAiResponse, AskAiRequest>({
			query: (payload) => ({
				url: 'ai/ask',
				method: 'POST',
				body: payload,
			}),
			async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled;
					const deletedIds = data.context.toolCalls.flatMap((toolCall) => toolCall.deletedIds ?? []);
					if (deletedIds.length) {
						dispatch(entriesDeleteMany(deletedIds));
					}
				} catch {
					// no-op
				}
			},
			invalidatesTags: (result, _error, { locationId }) => [
				'AiConversations',
				...(result?.context.toolCalls.some(({toolName}) => ENTRY_WRITE_TOOL_NAMES.has(toolName))
					? ['Repositories', 'Repository', 'RepositoryEntries', 'RepositoryEntry', 'RepositoryEntryCount'] as const
					: []),
				...(result?.context.toolCalls.some(({toolName}) => BOARD_WRITE_TOOL_NAMES.has(toolName))
					? ['BoardElements', { type: 'Notes', id: `location:${locationId ?? ''}` }] as const
					: []),
			],
		}),
		aiConversationsList: builder.query<AiConversationSummary[], void>({
			query: () => 'ai/conversations',
			providesTags: ['AiConversations'],
		}),
		aiConversationGet: builder.query<AiConversationWithMessages, string>({
			query: (id) => `ai/conversations/${id}`,
			extraOptions: {maxRetries: 0},
		}),
		aiConversationDelete: builder.mutation<void, string>({
			query: (id) => ({
				url: `ai/conversations/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['AiConversations'],
			extraOptions: {maxRetries: 0},
		}),
	}),
});

export const {
	useAskAiMutation,
	useAiConversationsListQuery,
	useLazyAiConversationGetQuery,
	useAiConversationDeleteMutation,
} = aiApi;
